import produce, { setAutoFreeze } from 'immer';
import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import shallowEqual from 'shallowequal';
import { compose, createStore as createStore$1, applyMiddleware } from 'redux';
import reduxThunk from 'redux-thunk';
import memoizerific from 'memoizerific';
var StoreContext = createContext();

var isStateObject = function isStateObject(x) {
  return x !== null && typeof x === 'object' && !Array.isArray(x);
};

var get = function get(path, target) {
  return path.reduce(function (acc, cur) {
    return isStateObject(acc) ? acc[cur] : undefined;
  }, target);
};

var set = function set(path, target, value) {
  path.reduce(function (acc, cur, idx) {
    if (idx + 1 === path.length) {
      acc[cur] = value;
    } else {
      acc[cur] = acc[cur] || {};
    }

    return acc[cur];
  }, target);
};

function useStore(mapState, dependencies) {
  if (dependencies === void 0) {
    dependencies = [];
  }

  var store = useContext(StoreContext);

  var _useState = useState(mapState(store.getState())),
      state = _useState[0],
      setState = _useState[1];

  var _useState2 = useState(null),
      error = _useState2[0],
      setError = _useState2[1]; // As our effect only fires on mount and unmount it won't have the state
  // changes visible to it, therefore we use a mutable ref to track this.


  var stateRef = useRef(state); // Helps avoid firing of events when unsubscribed, i.e. unmounted

  var isActive = useRef(true); // Tracks when a hooked component is unmounting

  var unmounted = useRef(false); // Throwing the error inline allows error boundaries the opportunity to
  // catch the error

  if (error) {
    throw error;
  }

  useEffect(function () {
    isActive.current = true;

    var calculateState = function calculateState() {
      if (!isActive.current) {
        return;
      }

      try {
        var newState = mapState(store.getState());

        if (newState === stateRef.current || isStateObject(newState) && isStateObject(stateRef.current) && shallowEqual(newState, stateRef.current)) {
          // Do nothing
          return;
        }

        stateRef.current = newState;
        setState(stateRef.current);
      } catch (err) {
        // see https://github.com/reduxjs/react-redux/issues/1179
        // There is a possibility mapState will fail as the props/state that
        // the component has received is stale. Therefore we will afford the
        // application a small window of opportunity, where if they unmount
        // the component or provide it with new "valid" props (which will
        // subsequently cause a refreshed subscription cycle) then we will not
        // throw an error.
        // This is by no means a robust solution. We should track the
        // associated issue in the hope for a more dependable solution.
        // Setting the listener as "inactive", this can only be changed if the
        // incoming dependencies are different (i.e. props have changed)
        isActive.current = false;
        setTimeout(function () {
          if (!unmounted.current && !isActive.current) {
            setError(err);
          }
        }, 200); // give a window of opportunity
      }
    };

    calculateState();
    var unsubscribe = store.subscribe(calculateState);
    return function () {
      isActive.current = false;
      unsubscribe();
    };
  }, dependencies); // This effect will set the ref value to indicate that the component has
  // unmounted

  useEffect(function () {
    return function () {
      unmounted.current = true;
    };
  }, []);
  return state;
}

function useActions(mapActions) {
  var store = useContext(StoreContext);
  return mapActions(store.dispatch);
}

function useDispatch() {
  var store = useContext(StoreContext);
  return store.dispatch;
}

function createTypedHooks() {
  return {
    useActions: useActions,
    useDispatch: useDispatch,
    useStore: useStore
  };
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

var actionNameSymbol = '__actionName__';
var actionSymbol = '__action__';
var listenSymbol = '__listen__';
var metaSymbol = '__meta__';
var reducerSymbol = '__reducer__';
var selectDependenciesSymbol = '__selectDependencies__';
var selectImpSymbol = '__selectImp__';
var selectStateSymbol = '__selectState__';
var selectSymbol = '__select__';
var thunkSymbol = '__thunk__';

var actionName = function actionName(action) {
  return action[actionNameSymbol];
};

var thunkStartName = function thunkStartName(action) {
  return action[actionNameSymbol] + "(started)";
};

var thunkCompleteName = function thunkCompleteName(action) {
  return action[actionNameSymbol] + "(completed)";
};

var thunkFailName = function thunkFailName(action) {
  return action[actionNameSymbol] + "(failed)";
};

var action = function action(fn) {
  fn[actionSymbol] = true;
  return fn;
};

var listen = function listen(fn) {
  fn[listenSymbol] = true;
  return fn;
};

var thunk = function thunk(fn) {
  fn[thunkSymbol] = true;
  return fn;
};

var select = function select(fn, dependencies) {
  fn[selectSymbol] = true;
  fn[selectDependenciesSymbol] = dependencies;
  fn[selectStateSymbol] = {};
  return fn;
};

var reducer = function reducer(fn) {
  fn[reducerSymbol] = true;
  return fn;
};

var maxSelectFnMemoize = 100;

var tick = function tick() {
  return new Promise(function (resolve) {
    return setTimeout(resolve);
  });
};

var errorToPayload = function errorToPayload(err) {
  if (err instanceof Error) {
    return {
      message: err.message,
      stack: err.stack
    };
  }

  if (typeof err === 'string') {
    return err;
  }

  return undefined;
};

function createStoreInternals(_ref) {
  var disableInternalSelectFnMemoize = _ref.disableInternalSelectFnMemoize,
      initialState = _ref.initialState,
      injections = _ref.injections,
      isRebind = _ref.isRebind,
      model = _ref.model,
      reducerEnhancer = _ref.reducerEnhancer,
      references = _ref.references;

  var wrapFnWithMemoize = function wrapFnWithMemoize(x) {
    return !disableInternalSelectFnMemoize && typeof x === 'function' ? memoizerific(maxSelectFnMemoize)(x) : x;
  };

  var defaultState = initialState || {};
  var actionThunks = {};
  var actionCreators = {};
  var actionCreatorDict = {};
  var customReducers = [];
  var selectorReducers = [];
  var listenDefinitions = [];
  var thunkListenersDict = {};
  var actionListenersDict = {};
  var actionReducersDict = {};

  var recursiveExtractDefsFromModel = function recursiveExtractDefsFromModel(current, parentPath) {
    return Object.keys(current).forEach(function (key) {
      var value = current[key];
      var path = [].concat(parentPath, [key]);
      var meta = {
        parent: parentPath,
        path: path
      };

      if (typeof value === 'function') {
        if (value[actionSymbol]) {
          var name = "@action." + path.join('.');
          value[actionNameSymbol] = name;
          value[metaSymbol] = meta; // Action Reducer

          var actionReducer = value;
          actionReducer[actionNameSymbol] = name;
          actionReducersDict[name] = actionReducer;

          var actionCreator = function actionCreator(payload) {
            var result = references.dispatch({
              type: actionReducer[actionNameSymbol],
              payload: payload
            });
            return result;
          };

          actionCreator[actionNameSymbol] = name;
          actionCreatorDict[name] = actionCreator;
          set(path, actionCreators, actionCreator);
        } else if (value[thunkSymbol]) {
          var _name = "@thunk." + path.join('.');

          value[actionNameSymbol] = _name; // Thunk Action

          var action = function action(payload) {
            return value(get(parentPath, actionCreators), payload, {
              dispatch: references.dispatch,
              getState: function getState() {
                return get(parentPath, references.getState());
              },
              getStoreState: references.getState,
              injections: injections,
              meta: meta
            });
          };

          set(path, actionThunks, action); // Thunk Action Creator

          var _actionCreator = function _actionCreator(payload) {
            return tick().then(function () {
              return references.dispatch({
                type: _name + "(started)",
                payload: payload
              });
            }).then(function () {
              return references.dispatch(function () {
                return action(payload);
              });
            }).then(function (result) {
              references.dispatch({
                type: _name + "(completed)",
                payload: payload
              });
              return result;
            }).catch(function (err) {
              references.dispatch({
                type: _name + "(failed)",
                payload: payload,
                error: errorToPayload(err)
              });
              throw err;
            });
          };

          _actionCreator[actionNameSymbol] = _name;
          actionCreatorDict[_name] = _actionCreator;
          set(path, actionCreators, _actionCreator);
        } else if (value[selectSymbol]) {
          value[selectStateSymbol] = {
            parentPath: parentPath,
            key: key,
            executed: false
          };
          selectorReducers.push(value);
        } else if (value[reducerSymbol]) {
          customReducers.push({
            path: path,
            reducer: value
          });
        } else if (value[listenSymbol]) {
          listenDefinitions.push(value);
          value[metaSymbol] = meta;
        } else if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn("Easy Peasy: Found a function at path " + path.join('.') + " in your model. Version 2 required that you wrap your action functions with the \"action\" helper");
        }
      } else if (isStateObject(value) && Object.keys(value).length > 0) {
        var existing = get(path, defaultState);

        if (existing == null) {
          set(path, defaultState, {});
        }

        recursiveExtractDefsFromModel(value, path);
      } else {
        // State
        var initialParentRef = get(parentPath, initialState);

        if (!isRebind && initialParentRef && key in initialParentRef) {
          set(path, defaultState, initialParentRef[key]);
        } else {
          set(path, defaultState, value);
        }
      }
    });
  };

  recursiveExtractDefsFromModel(model, []);
  selectorReducers.forEach(function (selector) {
    selector[selectImpSymbol] = function (state) {
      return wrapFnWithMemoize(selector(state));
    };
  });
  listenDefinitions.forEach(function (def) {
    var on = function on(target, handler) {
      if (typeof handler !== 'function') {
        return;
      }

      var meta = def[metaSymbol];
      handler[metaSymbol] = meta;

      if (!handler[actionSymbol] && !handler[thunkSymbol]) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line
          console.warn("Easy Peasy: you must provide either an \"action\" or \"thunk\" to your listeners. Found an invalid handler at \"" + meta.path.join('.') + "\"");
        }

        return;
      }

      var name;

      if (typeof target === 'function' && target[actionNameSymbol] && actionCreatorDict[target[actionNameSymbol]]) {
        if (target[thunkSymbol]) {
          name = thunkCompleteName(target);
        } else {
          name = target[actionNameSymbol];
        }
      } else if (typeof target === 'string') {
        name = target;
      }

      if (name) {
        if (handler[thunkSymbol]) {
          thunkListenersDict[name] = thunkListenersDict[name] || [];
          thunkListenersDict[name].push(handler);
        } else {
          actionListenersDict[name] = actionListenersDict[name] || [];
          actionListenersDict[name].push({
            path: meta.parent,
            handler: handler
          });
        }
      }
    };

    def(on);
  });

  var runSelectorReducer = function runSelectorReducer(state, selector) {
    var _selector$selectState = selector[selectStateSymbol],
        parentPath = _selector$selectState.parentPath,
        key = _selector$selectState.key,
        executed = _selector$selectState.executed;

    if (executed) {
      return state;
    }

    var dependencies = selector[selectDependenciesSymbol];
    var stateAfterDependencies = dependencies ? dependencies.reduce(runSelectorReducer, state) : state;
    var newState = stateAfterDependencies;

    if (parentPath.length > 0) {
      var target = get(parentPath, stateAfterDependencies);

      if (target) {
        if (!selector.prevState || selector.prevState !== get(parentPath, state)) {
          var newValue = selector[selectImpSymbol](target);
          newState = produce(state, function (draft) {
            var updateTarget = get(parentPath, draft);
            updateTarget[key] = newValue;
          });
          selector.prevState = get(parentPath, newState);
        }
      }
    } else if (!selector.prevState || selector.prevState !== state) {
      var _newValue = selector[selectImpSymbol](stateAfterDependencies);

      newState = produce(state, function (draft) {
        draft[key] = _newValue;
      });
      selector.prevState = newState;
    }

    selector[selectStateSymbol].executed = true;
    return newState;
  };

  var runSelectors = function runSelectors(state) {
    return selectorReducers.reduce(runSelectorReducer, state);
  };

  var createReducer = function createReducer() {
    var runActionReducerAtPath = function runActionReducerAtPath(state, action, actionReducer, path) {
      var current = get(path, state);

      if (path.length === 0) {
        return produce(state, function (_draft) {
          return actionReducer(_draft, action.payload);
        });
      }

      return produce(state, function (draft) {
        set(actionReducer[metaSymbol].parent, draft, produce(current, function (_draft) {
          return actionReducer(_draft, action.payload);
        }));
      });
    };

    var reducerForActions = function reducerForActions(state, action) {
      var actionReducer = actionReducersDict[action.type];

      if (actionReducer) {
        return runActionReducerAtPath(state, action, actionReducer, actionReducer[metaSymbol].parent);
      }

      return state;
    };

    var reducerForListeners = function reducerForListeners(state, action) {
      var actionListeners = actionListenersDict[action.type];

      if (actionListeners) {
        return actionListeners.reduce(function (newState, _ref2) {
          var path = _ref2.path,
              handler = _ref2.handler;
          return runActionReducerAtPath(newState, action, handler, path);
        }, state);
      }

      return state;
    };

    var reducerForCustomReducers = function reducerForCustomReducers(state, action) {
      return produce(state, function (draft) {
        customReducers.forEach(function (_ref3) {
          var p = _ref3.path,
              red = _ref3.reducer;
          var current = get(p, draft);
          set(p, draft, red(current, action));
        });
      });
    };

    var isInitial = true;

    var selectorsReducer = function selectorsReducer(state) {
      var stateAfterSelectors = runSelectors(state);
      isInitial = false;
      selectorReducers.forEach(function (selector) {
        selector[selectStateSymbol].executed = false;
      });
      return stateAfterSelectors;
    };

    var rootReducer = function rootReducer(state, action) {
      var stateAfterActions = reducerForActions(state, action);
      var stateAfterListeners = reducerForListeners(stateAfterActions, action);
      var stateAfterCustomReducers = reducerForCustomReducers(stateAfterListeners, action);

      if (state !== stateAfterCustomReducers || isInitial) {
        return selectorsReducer(stateAfterCustomReducers);
      }

      return stateAfterCustomReducers;
    };

    return rootReducer;
  };

  return {
    reducer: reducerEnhancer(createReducer()),
    defaultState: defaultState,
    actionCreators: actionCreators,
    thunkListenersDict: thunkListenersDict
  };
}

function createStore(model, options) {
  if (options === void 0) {
    options = {};
  }

  var _options = options,
      compose$1 = _options.compose,
      _options$devTools = _options.devTools,
      devTools = _options$devTools === void 0 ? true : _options$devTools,
      _options$disableInter = _options.disableInternalSelectFnMemoize,
      disableInternalSelectFnMemoize = _options$disableInter === void 0 ? false : _options$disableInter,
      _options$initialState = _options.initialState,
      initialState = _options$initialState === void 0 ? {} : _options$initialState,
      injections = _options.injections,
      _options$mockActions = _options.mockActions,
      mockActions = _options$mockActions === void 0 ? false : _options$mockActions,
      _options$middleware = _options.middleware,
      middleware = _options$middleware === void 0 ? [] : _options$middleware,
      _options$reducerEnhan = _options.reducerEnhancer,
      reducerEnhancer = _options$reducerEnhan === void 0 ? function (rootReducer) {
    return rootReducer;
  } : _options$reducerEnhan,
      _options$enhancers = _options.enhancers,
      enhancers = _options$enhancers === void 0 ? [] : _options$enhancers;

  var modelDefinition = _extends({}, model, {
    logFullState: thunk(function (actions, payload, _ref) {
      var getState = _ref.getState; // eslint-disable-next-line no-console

      console.log(JSON.stringify(getState(), null, 2));
    }),
    replaceState: action(function (state, payload) {
      return payload;
    })
  });

  var references = {};
  var mockedActions = [];

  var mockActionsMiddleware = function mockActionsMiddleware() {
    return function (next) {
      return function (action) {
        if (mockActions) {
          if (action) {
            mockedActions.push(action);
          }

          return undefined;
        }

        return next(action);
      };
    };
  };

  var dispatchThunkListeners = function dispatchThunkListeners(name, payload) {
    var listensForAction = references.internals.thunkListenersDict[name];
    return listensForAction && listensForAction.length > 0 ? Promise.all(listensForAction.map(function (listenForAction) {
      return listenForAction(get(listenForAction[metaSymbol].parent, references.internals.actionCreators), payload, {
        dispatch: references.dispatch,
        getState: function getState() {
          return get(listenForAction[metaSymbol].parent, references.getState());
        },
        getStoreState: references.getState,
        injections: injections,
        meta: listenForAction[metaSymbol]
      });
    })) : Promise.resolve();
  };

  var dispatchActionStringListeners = function dispatchActionStringListeners() {
    return function (next) {
      return function (action) {
        var result = next(action);

        if (references.internals.thunkListenersDict[action.type]) {
          dispatchThunkListeners(action.type, action.payload);
        }

        return result;
      };
    };
  };

  var composeEnhancers = compose$1 || (devTools && typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ : compose);

  var bindStoreInternals = function bindStoreInternals(state, isRebind) {
    if (isRebind === void 0) {
      isRebind = false;
    }

    references.internals = createStoreInternals({
      disableInternalSelectFnMemoize: disableInternalSelectFnMemoize,
      initialState: state,
      injections: injections,
      model: modelDefinition,
      reducerEnhancer: reducerEnhancer,
      references: references,
      isRebind: isRebind
    });
  };

  bindStoreInternals(initialState);
  var store = createStore$1(references.internals.reducer, references.internals.defaultState, composeEnhancers.apply(void 0, [applyMiddleware.apply(void 0, [reduxThunk, dispatchActionStringListeners].concat(middleware, [mockActionsMiddleware]))].concat(enhancers)));

  store.getMockedActions = function () {
    return [].concat(mockedActions);
  };

  store.clearMockedActions = function () {
    mockedActions = [];
  };

  references.dispatch = store.dispatch;
  references.getState = store.getState; // attach the action creators to dispatch

  var bindActionCreators = function bindActionCreators(actionCreators) {
    Object.keys(store.dispatch).forEach(function (actionsKey) {
      delete store.dispatch[actionsKey];
    });
    Object.keys(actionCreators).forEach(function (key) {
      store.dispatch[key] = actionCreators[key];
    });
  };

  bindActionCreators(references.internals.actionCreators);

  var rebindStore = function rebindStore() {
    bindStoreInternals(store.getState(), true);
    store.replaceReducer(references.internals.reducer);
    store.dispatch.replaceState(references.internals.defaultState);
    bindActionCreators(references.internals.actionCreators);
  };

  store.addModel = function (key, modelForKey) {
    if (modelDefinition[key] && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn("The store model already contains a model definition for \"" + key + "\"");
      store.removeModel(key);
    }

    modelDefinition[key] = modelForKey;
    rebindStore();
  };

  store.removeModel = function (key) {
    if (!modelDefinition[key]) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn("The store model does not contain a model definition for \"" + key + "\"");
      }

      return;
    }

    delete modelDefinition[key];
    rebindStore();
  };

  return store;
}

var StoreProvider = function StoreProvider(_ref) {
  var children = _ref.children,
      store = _ref.store;
  return React.createElement(StoreContext.Provider, {
    value: store
  }, children);
};
/**
 * immer is an implementation detail, so we are not going to use its auto freeze
 * behaviour, which throws errors if trying to mutate state. It's also risky
 * for production builds as has a perf overhead.
 *
 * @see https://github.com/mweststrate/immer#auto-freezing
 */


setAutoFreeze(false);
export { StoreProvider, action, actionName, createStore, createTypedHooks, listen, reducer, select, thunk, thunkCompleteName, thunkFailName, thunkStartName, useActions, useDispatch, useStore };